import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _times from 'lodash/times';
import _clone from 'lodash/clone';
import _debounce from 'lodash/debounce';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import I18n from 'i18n-js';
import PropTypes from 'prop-types';
import BookOrderForm from 'views/v2/home/book-order-form';
import { FIELD_NAME, MODE } from 'views/v2/home/constants';
import LocationDrawer from 'components/v2/location-drawer/location-drawer';
import OrderSummaryWidget from 'components/v2/order-summary-widget/order-summary-widget';
import BackButton from 'components/v2/back-button/back-button';
import DivButton from 'components/v2/div-button/div-button';
import { MAX_ORDERS_PER_BATCH } from 'constants/booking-constants';
import { Affix } from 'grab-ui';
import { trackClick, event, cat, custom } from 'common/utils/instrumentation';
import { getCompanyID } from 'helper/user';
import {
  createOrderActionV2,
  updateOrderAction,
  updateOrderShallowAction,
  fetchServiceTypesAction,
  updateBulkOrderAction,
  updateBulkOrderShallowAction,
} from 'actions/booking-actions';
import {
  toggleLocationDrawerAction,
  pushNotificationAction,
  showFailedPaymentsRetryModalAction,
} from 'actions/app-actions';
import { paymentMethodSelector } from 'selectors/booking-selectors';
import {
  userInfoSelector,
  isCashPaymentDisabledSelector,
  isCashlessPaymentDisabledSelector,
} from 'selectors/app-selector';
import {
  toPlaceOrder,
  getSelectedServiceType,
  getQuotesToRender,
} from 'helper/order';
import {
  scribeTrackEvent,
  getCommonFacet,
  scribeTrackOrderPageCreateBooking,
  SCRIBE_STATE_NAMES,
  SCRIBE_DELIVERY_DETAILS_EVENTS,
  SCRIBE_EVENT_PARAMS,
} from 'applications/scribe/instrumentations/track';

import { pushGABookDeliveriesEvent } from 'helper/ga-helper';

import { getTotalAmountFromQuotes } from 'helper/quote';

import css from './bulk-order-page.less';

const orderFormViewIdPrefix = 'orderForm#';
class BulkOrderPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      onConfirmUpdateLocation: () => {},
      focusedOrderIndex: 0,
      allowAddOrder: true,
    };
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevProps.orders.length !== this.props.orders.length) {
      this.setAllowAddOrder();
    }
  }

  onClickPickupLocation = index => () => {
    const { orders, toggleLocationDrawer } = this.props;
    const location = _get(orders, `[${index}].senderLocation`, {});
    toggleLocationDrawer([location], true);
    this.setState({
      onConfirmUpdateLocation: this.onConfirmSenderLocation(index),
    });
  };

  onClickDropoffLocation = orderIndex => recipientIndex => () => {
    const { orders, toggleLocationDrawer } = this.props;
    const location = _get(
      orders,
      `[${orderIndex}].recipientLocations[${recipientIndex}]`,
    );
    toggleLocationDrawer([location]);
    this.setState({
      onConfirmUpdateLocation: this.onConfirmRecipientLocation({
        orderIndex,
        recipientIndex,
      }),
    });
  };

  onConfirmSenderLocation = orderIndex => newLocations => {
    const { orders, updateBulkOrder } = this.props;
    const newOrder = {
      ...orders[orderIndex],
      senderLocation: newLocations[0],
    };
    updateBulkOrder({ index: orderIndex, order: newOrder });
  };

  onConfirmRecipientLocation = ({
    orderIndex,
    recipientIndex,
  }) => newLocations => {
    const { orders, updateBulkOrder } = this.props;
    const updatedOrder = {
      ...orders[orderIndex],
      // bulk page only support 1
      recipientLocations: [newLocations[0]],
    };
    updateBulkOrder({ index: orderIndex, order: updatedOrder });
  };

  onClickPlaceOrder = _debounce(() => {
    const {
      orders,
      userInfo,
      payments,
      createOrder,
      onOrderCreateComplete,
    } = this.props;
    const orderToPlace = toPlaceOrder(orders, userInfo, payments);

    createOrder(orderToPlace, () => {
      onOrderCreateComplete();
    });

    // Track Create booking for each order
    orders.map(order => {
      scribeTrackOrderPageCreateBooking(order, userInfo, MODE.MULTI_BOOK);
    });

    const quotes = getQuotesToRender(orders);
    const totalAmount = getTotalAmountFromQuotes(quotes);

    pushGABookDeliveriesEvent(
      userInfo,
      MODE.MULTI_BOOK,
      totalAmount,
      orders.reduce(
        (deliveryTypeText, order) =>
          deliveryTypeText
            .concat(getSelectedServiceType(order).name)
            .concat(','),
        '',
      ),
      orders.length,
    );
  }, 500);

  onClickOrderNumber = index => {
    const { userInfo } = this.props;
    trackClick(
      event.DELIVERY_DETAILS_CLICKS,
      cat.GE_DELIVERY_DETAILS,
      'LEFT_BAR',
      {
        [custom.COMPANY_ID]: getCompanyID(userInfo),
        DELIVERY_NUMBER: index,
      },
    );
    scribeTrackEvent({
      stateName: SCRIBE_STATE_NAMES.EXPRESS_DELIVERY_DETAILS_WEB,
      eventName: SCRIBE_DELIVERY_DETAILS_EVENTS.LEFT_BAR,
      facets: {
        ...getCommonFacet(userInfo),
        [SCRIBE_EVENT_PARAMS.DELIVERY_NUMBER]: index,
      },
    });

    this.setState({ focusedOrderIndex: index }, () => {
      this.scrollToOrder(index);
    });
  };

  /*
   * Add order according to given index
   * when index = -1: add new empty order
   * when index >= 0: add (duplicate) new order same as orders[index]
  */
  onAddOrder = index => {
    if (!this.state.allowAddOrder) return;

    const { orders, updateBulkOrder } = this.props;
    const newOrder =
      index === -1 ? this.newEmptyOrder() : _clone(orders[index]);
    if (!newOrder) return;

    // newOrder.quote = {};
    updateBulkOrder({
      index: orders.length,
      order: newOrder,
    });
    // scroll to bottom to show the new added order
    this.scrollToOrder(orders.length);
  };

  onRemoveOrder = index => () => {
    const { orders, updateBulkOrderShallow, userInfo } = this.props;
    trackClick(
      event.DELIVERY_DETAILS_CLICKS,
      cat.GE_DELIVERY_DETAILS,
      'DELETE_DELIVERY',
      {
        [custom.COMPANY_ID]: getCompanyID(userInfo),
        DELIVERY_NUMBER: index,
      },
    );
    scribeTrackEvent({
      stateName: SCRIBE_STATE_NAMES.EXPRESS_DELIVERY_DETAILS_WEB,
      eventName: SCRIBE_DELIVERY_DETAILS_EVENTS.DELETE_DELIVERY,
      facets: {
        ...getCommonFacet(userInfo),
        [SCRIBE_EVENT_PARAMS.DELIVERY_NUMBER]: index,
      },
    });

    const newOrders = orders.filter(
      (_order, currentIndex) => currentIndex !== index,
    );
    updateBulkOrderShallow({
      orders: newOrders,
    });
  };

  onCopyOrder = index => () => {
    const { userInfo } = this.props;
    trackClick(
      event.DELIVERY_DETAILS_CLICKS,
      cat.GE_DELIVERY_DETAILS,
      'DUPLICATE_DELIVERY',
      {
        [custom.COMPANY_ID]: getCompanyID(userInfo),
        DELIVERY_NUMBER: index,
      },
    );
    scribeTrackEvent({
      stateName: SCRIBE_STATE_NAMES.EXPRESS_DELIVERY_DETAILS_WEB,
      eventName: SCRIBE_DELIVERY_DETAILS_EVENTS.DUPLICATE_DELIVERY,
      facets: {
        ...getCommonFacet(userInfo),
        [SCRIBE_EVENT_PARAMS.DELIVERY_NUMBER]: index,
      },
    });
    this.onAddOrder(index);
  };

  setAllowAddOrder = () => {
    const { orders } = this.props;
    this.setState({
      allowAddOrder: orders.length < MAX_ORDERS_PER_BATCH,
    });
  };

  addOrderBottom = index => {
    const { userInfo } = this.props;
    trackClick(
      event.DELIVERY_DETAILS_CLICKS,
      cat.GE_DELIVERY_DETAILS,
      'ADD_DELIVERY_BOTTOM',
      {
        [custom.COMPANY_ID]: getCompanyID(userInfo),
        TOTAL_DELIVERIES: _get(this.props, 'orders.length'),
      },
    );
    scribeTrackEvent({
      stateName: SCRIBE_STATE_NAMES.EXPRESS_DELIVERY_DETAILS_WEB,
      eventName: SCRIBE_DELIVERY_DETAILS_EVENTS.ADD_DELIVERY_BOTTOM,
      facets: {
        ...getCommonFacet(userInfo),
        [SCRIBE_EVENT_PARAMS.TOTAL_DELIVERIES]: _get(
          this.props,
          'orders.length',
        ),
      },
    });

    this.onAddOrder(index);
  };

  addOrderSideBar = index => {
    const { userInfo } = this.props;
    trackClick(
      event.DELIVERY_DETAILS_CLICKS,
      cat.GE_DELIVERY_DETAILS,
      'ADD_DELIVERY_LEFT',
      {
        [custom.COMPANY_ID]: getCompanyID(userInfo),
        TOTAL_DELIVERIES: _get(this.props, 'orders.length'),
      },
    );
    scribeTrackEvent({
      stateName: SCRIBE_STATE_NAMES.EXPRESS_DELIVERY_DETAILS_WEB,
      eventName: SCRIBE_DELIVERY_DETAILS_EVENTS.ADD_DELIVERY_LEFT,
      facets: {
        ...getCommonFacet(userInfo),
        [SCRIBE_EVENT_PARAMS.TOTAL_DELIVERIES]: _get(
          this.props,
          'orders.length',
        ),
      },
    });

    this.onAddOrder(index);
  };

  newEmptyOrder = () => {
    const { orders } = this.props;
    if (_isEmpty(orders)) return null;

    return {
      serviceTypes: _get(orders, '[0].serviceTypes'),
      [FIELD_NAME.SERVICE_TYPE]: _get(orders, `[0].${FIELD_NAME.SERVICE_TYPE}`),
    };
  };

  scrollToOrder = index => {
    setTimeout(() => {
      const targetOrderFormElement = document.getElementById(
        `${orderFormViewIdPrefix}${index}`,
      );
      if (targetOrderFormElement) {
        targetOrderFormElement.scrollIntoView();
      }
    });
  };

  renderBookOrderForms = () => {
    const {
      mode,
      orders,
      payments,
      updateOrder,
      updateOrderShallow,
      updateBulkOrder,
      updateBulkOrderShallow,
      userInfo,
      isCashPaymentDisabled,
      isCashlessPaymentDisabled,
      pushNotification,
      showFailedPaymentsRetryModal,
    } = this.props;
    const { allowAddOrder } = this.state;

    return _times(orders.length, index => {
      const order = orders[index];

      return (
        <div
          id={`${orderFormViewIdPrefix}${index}`}
          key={`${orderFormViewIdPrefix}${index}`}
        >
          <BookOrderForm
            index={index}
            mode={mode}
            order={order}
            orders={orders}
            payments={payments}
            updateOrder={updateOrder}
            updateOrderShallow={updateOrderShallow}
            updateBulkOrder={updateBulkOrder}
            updateBulkOrderShallow={updateBulkOrderShallow}
            onClickPickupLocation={this.onClickPickupLocation(index)}
            onClickDropoffLocation={this.onClickDropoffLocation(index)}
            onDeleteBulkForm={this.onRemoveOrder(index)}
            onCopyBulkForm={this.onCopyOrder(index)}
            userInfo={userInfo}
            allowCopy={allowAddOrder}
            isCashPaymentDisabled={isCashPaymentDisabled}
            isCashlessPaymentDisabled={isCashlessPaymentDisabled}
            pushNotification={pushNotification}
            onRetryFailedPayments={showFailedPaymentsRetryModal}
          />
        </div>
      );
    });
  };

  render() {
    const {
      onConfirmUpdateLocation,
      focusedOrderIndex,
      allowAddOrder,
    } = this.state;
    const { onClickBack, orders, userInfo } = this.props;

    return (
      <div>
        <BackButton onClick={onClickBack} />
        <h4 className={css.pageHeader}>
          {I18n.t('v2.homepage.manualOrder.heading.multipleBook')}
        </h4>
        <div className={css.orderPanel}>
          <div className={css.hangingOnLeft}>
            <Affix offset={24}>
              <div className={css.orderQtyContainer}>
                <div className={css.orderQty}>
                  <div className={css.numbers}>
                    {_times(orders.length, index => (
                      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                      <h6
                        className={
                          index === focusedOrderIndex
                            ? css.focusedNumberText
                            : null
                        }
                        key={`number-${index}`}
                        onClick={() => this.onClickOrderNumber(index)}
                        onKeyDown={null}
                      >
                        {index + 1}
                      </h6>
                    ))}
                  </div>
                  <DivButton
                    className={classnames(css.addOrderBtn, {
                      [css.addOrderBtnDisabled]: !allowAddOrder,
                    })}
                    onClick={() => this.addOrderSideBar(-1)}
                  >
                    <span>+</span>
                  </DivButton>
                </div>
              </div>
            </Affix>
          </div>
          <div className={css.orderForms}>
            {this.renderBookOrderForms()}
            {allowAddOrder && (
              <DivButton
                className={css.addOrderButton}
                onClick={() => this.addOrderBottom(-1)}
              >
                <span>{`+ ${I18n.t(
                  'v2.homepage.manualOrder.button.addNewDelivery',
                )}`}</span>
              </DivButton>
            )}
          </div>
          <OrderSummaryWidget
            isForBulk
            orders={orders}
            onClickPlaceOrder={this.onClickPlaceOrder}
            userInfo={userInfo}
          />
        </div>
        <LocationDrawer
          onUpdate={onConfirmUpdateLocation}
          allowMultipleAddresses={false}
        />
      </div>
    );
  }
}

BulkOrderPage.propTypes = {
  mode: PropTypes.string.isRequired,
  orders: PropTypes.arrayOf(PropTypes.shape({})),
  payments: PropTypes.arrayOf(PropTypes.shape({})),
  userInfo: PropTypes.shape({}).isRequired,
  createOrder: PropTypes.func.isRequired,
  updateOrder: PropTypes.func.isRequired,
  updateOrderShallow: PropTypes.func.isRequired,
  updateBulkOrder: PropTypes.func.isRequired,
  updateBulkOrderShallow: PropTypes.func.isRequired,
  onClickBack: PropTypes.func.isRequired,
  toggleLocationDrawer: PropTypes.func.isRequired,
  onOrderCreateComplete: PropTypes.func.isRequired,
  isCashPaymentDisabled: PropTypes.bool,
  pushNotification: PropTypes.func.isRequired,
  isCashlessPaymentDisabled: PropTypes.bool.isRequired,
  showFailedPaymentsRetryModal: PropTypes.func.isRequired,
};

BulkOrderPage.defaultProps = {
  orders: [],
  payments: [],
  isCashPaymentDisabled: false,
};

const mapStateToProps = state => ({
  order: state.booking.order,
  orders: state.booking.orders,
  payments: paymentMethodSelector(state),
  userInfo: userInfoSelector(state),
  isCashPaymentDisabled: isCashPaymentDisabledSelector(state),
  isCashlessPaymentDisabled: isCashlessPaymentDisabledSelector(state),
});

const mapDispatchToProps = {
  createOrder: createOrderActionV2,
  updateOrder: updateOrderAction,
  updateOrderShallow: updateOrderShallowAction,
  updateBulkOrder: updateBulkOrderAction,
  updateBulkOrderShallow: updateBulkOrderShallowAction,
  fetchServiceTypes: fetchServiceTypesAction,
  toggleLocationDrawer: toggleLocationDrawerAction,
  pushNotification: pushNotificationAction,
  showFailedPaymentsRetryModal: showFailedPaymentsRetryModalAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(BulkOrderPage);
